import { useCallback, useEffect, useState } from "react";
import { useLocation, useParams, useSearch } from "wouter";

import { apiClient } from "../api/client.js";

interface DeviceEvent {
  id: string;
  device_name: string;
  venue_name: string;
  event_name: string;
  properties: Record<string, unknown>;
  client_timestamp: string;
  received_at: string;
}

interface Pagination {
  page: number;
  per_page: number;
  total: number;
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

function formatProperties(props: Record<string, unknown>): string {
  const entries = Object.entries(props);
  if (entries.length === 0) return "-";
  return entries.map(([k, v]) => `${k}: ${JSON.stringify(v)}`).join(", ");
}

export function DeviceEvents() {
  const { id: deviceId } = useParams<{ id: string }>();
  const search = useSearch();
  const [, navigate] = useLocation();
  const params = new URLSearchParams(search);
  const currentPage = Math.max(1, parseInt(params.get("page") ?? "1", 10));

  const [events, setEvents] = useState<DeviceEvent[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [deviceName, setDeviceName] = useState<string>("");
  const [venueName, setVenueName] = useState<string>("");

  const fetchEvents = useCallback(async () => {
    if (!deviceId) return;
    setLoading(true);
    try {
      const res = await apiClient["/api/v1/admin/devices/{id}/events"]["get"]({
        parameters: {
          id: deviceId,
          page: currentPage,
          per_page: 25,
        },
        body: null,
      });
      if (res.code === 200) {
        const body = res.body as { data: DeviceEvent[]; pagination: Pagination };
        setEvents(body.data);
        setPagination(body.pagination);
        if (body.data.length > 0) {
          setDeviceName(body.data[0].device_name);
          const eventWithVenue = body.data.find((e) => e.venue_name);
          setVenueName(eventWithVenue?.venue_name ?? "");
        }
      }
    } catch {
      // Ignore
    } finally {
      setLoading(false);
    }
  }, [deviceId, currentPage]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  function setPage(page: number) {
    const p = new URLSearchParams(search);
    p.set("page", String(page));
    navigate("?" + p.toString(), { replace: true });
  }

  const totalPages = pagination
    ? Math.max(1, Math.ceil(pagination.total / pagination.per_page))
    : 1;

  return (
    <main className="p-6">
      <div className="mb-6">
        <a
          href="/"
          className="text-sm text-text-muted hover:text-accent transition-colors no-underline"
        >
          &larr; Back to Dashboard
        </a>
        <h1 className="text-2xl font-bold text-text-primary mt-2">
          Device Events{deviceName ? `: ${deviceName}` : ""}
        </h1>
        {!loading &&
          (venueName ? (
            <p className="text-sm text-text-muted mt-1">{venueName}</p>
          ) : (
            events.length > 0 && <p className="text-sm text-text-muted mt-1">Unassigned</p>
          ))}
      </div>

      {loading && <p className="text-text-muted text-sm">Loading events...</p>}

      {!loading && events.length === 0 && (
        <div className="bg-surface-raised border border-border rounded-lg p-6 text-center">
          <p className="text-text-secondary">No events recorded for this device.</p>
        </div>
      )}

      {!loading && events.length > 0 && (
        <>
          <div className="grid gap-3">
            {events.map((e) => (
              <div
                key={e.id}
                className="bg-surface-raised border border-border rounded-lg p-4 flex items-center justify-between"
              >
                <div>
                  <div className="font-medium text-text-primary font-mono text-sm">
                    {e.event_name}
                  </div>
                  <div className="text-xs text-text-muted mt-1 max-w-lg truncate">
                    {formatProperties(e.properties)}
                  </div>
                </div>
                <div className="text-sm text-text-muted whitespace-nowrap ml-4">
                  {formatTime(e.client_timestamp)}
                </div>
              </div>
            ))}
          </div>

          {pagination && totalPages > 1 && (
            <div className="flex items-center justify-between mt-6">
              <button
                onClick={() => setPage(currentPage - 1)}
                disabled={currentPage <= 1}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-text-muted">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => setPage(currentPage + 1)}
                disabled={currentPage >= totalPages}
                className="px-3 py-1.5 text-sm rounded-md border border-border text-text-secondary hover:text-text-primary transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </main>
  );
}
